/**
 * Importar extracto (móvil): lógica pura del flujo, sin React. Qué archivos
 * se aceptan, cómo se convierten los movimientos leídos en movimientos de la
 * app y cómo se separa la vista previa en nuevos y repetidos.
 */
import { amountToInteger } from '@actual-app/core/shared/util';
import type { ImportTransactionEntity } from '@actual-app/core/types/models';

import { leerFecha } from './leerExtracto';
import type { MovimientoLeido } from './leerExtracto';

/** Lo que se puede elegir en el selector de archivos. */
export const EXTENSIONES = [
  'csv',
  'tsv',
  'txt',
  'xls',
  'xlsx',
  'n43',
  'q43',
  'aeb',
  'ofx',
  'qfx',
  'qif',
  'xml',
];

/**
 * `tabla`: filas sin interpretar (CSV, Excel) que se leen aquí.
 * `estructurado`: el servidor ya da fecha, concepto e importe (Norma 43,
 * OFX, QIF, CAMT).
 */
export type TipoArchivo = 'tabla' | 'estructurado';

export function extension(ruta: string): string {
  const m = ruta.match(/\.([^./\\]+)$/);
  return m ? m[1].toLowerCase() : '';
}

export function tipoArchivo(ruta: string): TipoArchivo | null {
  const ext = extension(ruta);
  if (['csv', 'tsv', 'xls', 'xlsx'].includes(ext)) {
    return 'tabla';
  }
  if (EXTENSIONES.includes(ext)) {
    return 'estructurado';
  }
  return null;
}

/** Nombre del archivo sin la carpeta (la ruta interna no dice nada). */
export function nombreArchivo(ruta: string): string {
  return ruta.split(/[/\\]/).pop() ?? ruta;
}

/** ¿El texto salió con «�»? Entonces no era UTF-8 (suele ser Windows-1252). */
export function tieneCaracteresRotos(filas: string[][]): boolean {
  return filas.some(fila => fila.some(celda => celda.includes('�')));
}

/** Lo que devuelve el servidor para Norma 43, OFX, QIF o CAMT. */
export type MovimientoEstructurado = {
  amount: number | null;
  date: string | null;
  payee_name?: string | null;
  imported_payee?: string | null;
  notes?: string | null;
  imported_id?: string;
};

/**
 * Movimientos ya interpretados por el servidor. QIF trae las fechas tal
 * cual: se leen como fechas españolas si no son ISO.
 */
export function movimientosEstructurados(
  lista: MovimientoEstructurado[],
): MovimientoLeido[] {
  const movimientos: MovimientoLeido[] = [];
  for (const m of lista) {
    const fecha = m.date ? leerFecha(m.date) : null;
    if (fecha == null || m.amount == null || isNaN(m.amount)) {
      continue;
    }
    movimientos.push({
      fecha,
      concepto: (m.payee_name || m.imported_payee || m.notes || '').trim(),
      importe: m.amount,
      ...(m.imported_id ? { idBanco: m.imported_id } : null),
    });
  }
  return movimientos;
}

/** Del 15 sep al 15 oct: primera y última fecha. */
export function rangoFechas(
  movimientos: MovimientoLeido[],
): { desde: string; hasta: string } | null {
  if (movimientos.length === 0) {
    return null;
  }
  const fechas = movimientos.map(m => m.fecha).sort();
  return { desde: fechas[0], hasta: fechas[fechas.length - 1] };
}

/**
 * Movimiento listo para `transactions-import`; `trx_id` empareja cada uno con
 * su entrada de la vista previa.
 */
export type MovimientoAImportar = ImportTransactionEntity & { trx_id: string };

export function idImportacion(indice: number): string {
  return `extracto-${indice}`;
}

export function aImportar(
  cuenta: string,
  movimientos: MovimientoLeido[],
): MovimientoAImportar[] {
  return movimientos.map((m, i) => ({
    trx_id: idImportacion(i),
    account: cuenta,
    date: m.fecha,
    amount: amountToInteger(m.importe),
    payee_name: m.concepto,
    imported_payee: m.concepto,
    ...(m.idBanco ? { imported_id: m.idBanco } : null),
    // Sale del banco: ya está confirmado.
    cleared: true,
  }));
}

/** Una entrada de la vista previa del servidor (`updatedPreview`). */
export type EntradaVistaPrevia = {
  transaction: {
    trx_id?: string;
    payee_name?: string | null;
    category?: string | null;
  };
  existing?: unknown;
  ignored?: boolean;
  tombstone?: boolean;
};

export type FilaVista = {
  id: string;
  fecha: string;
  /** Nombre como quedará (las reglas pueden haberlo cambiado). */
  nombre: string;
  importe: number;
  categoria: string | null;
  /** Ya estaba en la cuenta (o se borró): no se vuelve a traer. */
  repetido: boolean;
  /**
   * Del día del saldo inicial o de antes: ya cuenta en ese saldo, así que
   * traerlo lo contaría dos veces y descuadraría la cuenta y los meses
   * pasados. No se trae (salvo como historia).
   */
  antes: boolean;
  /**
   * De antes del primer mes con Rumbo: con «Traer la historia» entra como
   * historia y el saldo inicial se recalcula hacia atrás.
   */
  historia: boolean;
};

/**
 * Junta lo leído con la vista previa: lo que coincide con un movimiento que
 * ya estaba (o que se borró) es repetido; lo del día del saldo inicial o de
 * antes se queda fuera; lo demás es nuevo, con la categoría que le ponen las
 * reglas. Ordenado del más reciente al más antiguo, como la lista de
 * movimientos.
 */
export function filasVista(
  movimientos: MovimientoLeido[],
  vistaPrevia: EntradaVistaPrevia[],
  fechaSaldoInicial: string | null = null,
  /** Primer mes con Rumbo (`YYYY-MM`): lo de antes puede ser historia. */
  inicioHistoria: string | null = null,
): FilaVista[] {
  const porId = new Map(
    vistaPrevia
      .filter(e => e.transaction.trx_id)
      .map(e => [e.transaction.trx_id as string, e]),
  );
  return movimientos
    .map((m, i) => {
      const id = idImportacion(i);
      const entrada = porId.get(id);
      const repetido = !!(
        entrada?.existing ||
        entrada?.ignored ||
        entrada?.tombstone
      );
      const antes =
        !repetido && fechaSaldoInicial != null && m.fecha <= fechaSaldoInicial;
      return {
        id,
        fecha: m.fecha,
        nombre: entrada?.transaction.payee_name || m.concepto,
        importe: m.importe,
        categoria: entrada?.transaction.category ?? null,
        repetido,
        antes,
        historia:
          antes && inicioHistoria != null && m.fecha < `${inicioHistoria}-01`,
      };
    })
    .sort((a, b) => b.fecha.localeCompare(a.fecha));
}

export type Resumen = {
  nuevos: number;
  conCategoria: number;
  repetidos: number;
  /** Del día del saldo inicial o de antes (sin contar la historia). */
  anteriores: number;
  /** De antes del primer mes con Rumbo: se puede traer como historia. */
  historia: number;
};

export function resumen(
  filas: FilaVista[],
  fuera: Set<string>,
  conHistoria = false,
): Resumen {
  const nuevas = filas.filter(
    f => !f.historia && seTrae(f) && !fuera.has(f.id),
  );
  return {
    nuevos: nuevas.length,
    conCategoria: nuevas.filter(f => f.categoria != null).length,
    repetidos: filas.filter(f => f.repetido).length,
    anteriores: filas.filter(f => f.antes && !(conHistoria && f.historia))
      .length,
    historia: filas.filter(f => f.historia).length,
  };
}

/**
 * ¿Se puede traer? Ni lo repetido ni lo de antes del saldo inicial (salvo
 * la historia, si se pide).
 */
export function seTrae(f: FilaVista, conHistoria = false): boolean {
  return !f.repetido && (!f.antes || (conHistoria && f.historia));
}

/** Lo que se manda al importar: los nuevos que no se han dejado fuera. */
export function seleccionImportar(
  todos: MovimientoAImportar[],
  filas: FilaVista[],
  fuera: Set<string>,
  conHistoria = false,
): ImportTransactionEntity[] {
  const traer = new Set(
    filas
      .filter(f => seTrae(f, conHistoria) && !fuera.has(f.id))
      .map(f => f.id),
  );
  // trx_id solo sirve para emparejar la vista previa: la tabla no lo tiene.
  return todos
    .filter(m => traer.has(m.trx_id))
    .map(({ trx_id: _trx_id, ...movimiento }) => movimiento);
}
